import {Injectable} from '@angular/core';
import {NavigationCancel, NavigationEnd, NavigationError, Router} from '@angular/router';
import {ModalController} from '@ionic/angular/standalone';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subscription,
  catchError,
  combineLatest,
  debounceTime,
  defer,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  startWith,
  switchMap,
  take,
  tap,
  timeout,
} from 'rxjs';

import {LocalStorageService} from '../local-storage.service';
import {LogoutManagerService} from '../logout-manager.service';
import {GlobalManagerComuneService} from '../pratiche/global-manager-comune.service';
import {deveSaltareProfiloCittadino} from '../../shared/utils/ruoli-token.util';
import {
  DocumentiComuneModalComponent,
  DOCUMENTI_COMUNE_ESITO_ACCETTATO,
  DOCUMENTI_COMUNE_ESITO_ESCI,
  DOCUMENTI_COMUNE_ESITO_SISTEMA,
} from '../../shared/modals/documenti-comune-modal/documenti-comune-modal.component';
import {DocumentiComuneApi, DocumentiDaAccettare} from './documenti-comune.api';

/** Contesto per cui il gate interroga il backend: stesso utente e stesso Comune → una sola chiamata. */
interface ContestoGate {
  userId: string;
  slug: string;
}

/** Attesa massima di una pagina d'arrivo ammessa prima di rinunciare a mostrare la modale. */
const ATTESA_ROUTER_MAX_MS = 15_000;

/** Id della modale del gate: la chiusura passa da qui, non dalla modale «in cima» allo stack. */
export const DOCUMENTI_COMUNE_GATE_MODAL_ID = 'documenti-comune-gate-modal';

/**
 * Pagine d'arrivo dell'area autenticata del cittadino: solo qui il gate può presentarsi. Restano
 * fuori `/login`, la registrazione, il callback SPID/CIE e l'onboarding (completa-profilo,
 * scegli-comune): un'accettazione bloccante lì annullerebbe le loro navigazioni imperative.
 */
const ROTTA_AMMESSA = /^\/pages(\/|$)/;

/**
 * Gate dei «documenti del Comune»: apre la modale bloccante di accettazione quando il cittadino ha
 * documenti pubblicati del Comune corrente che non ha ancora accettato.
 *
 * <p><b>Quando scatta.</b> Ascolta insieme la sessione (`checkLoggedUser$`) e il Comune
 * selezionato (`GlobalManagerComuneService.comuneSelezionato$`). Interroga il backend solo per un
 * cittadino (non un operatore di backoffice) con una sessione attiva e uno slug, e solo quando la
 * coppia (utente, slug) cambia. Questo copre in un punto solo: avvio dell'app (ripristino del
 * Comune in app-init), login (scelta del Comune dopo l'accesso), scelta e cambio Comune, deep link.
 * Non dipende da una rotta, che `respectBrowserUrl` potrebbe scavalcare.
 *
 * <p><b>Fail-open.</b> Qualunque errore (rete, 4xx, 5xx, timeout) non apre nulla né naviga: il blocco
 * è solo lato app e un problema transitorio non deve chiudere l'accesso. Se il Comune non ha documenti
 * pubblicati la risposta è `da_accettare:false` e non succede niente.
 *
 * <p><b>Navigazioni.</b> Prima di presentare la modale attende che il router sia fermo su una pagina
 * d'arrivo ammessa (`/pages/**`) e che il profilo sia caricato: un `navigateRoot` ancora in corso (es.
 * dalla scelta del Comune) o una pagina con `ModalGuard` (es. `/login`, anche con un Comune residuo di
 * una sessione scaduta) annullerebbe la navigazione successiva. Su una rotta esclusa aspetta il
 * `NavigationEnd` seguente, entro lo stesso limite di tempo (poi fail-open).
 * La modale dichiara `isNotDismissableWithBack` (il tasto indietro non la chiude) e
 * `isAutoSkipSafe` (le navigazioni automatiche non la chiudono e non vengono annullate).
 *
 * <p><b>Logout.</b> Quando la sessione cade o il contesto cambia la modale si chiude e lo stato si
 * azzera da sé (stesso approccio di `PushContentService`: nessun reset da ogni punto di teardown).
 */
@Injectable({providedIn: 'root'})
export class DocumentiComuneGateService {
  private avviato = false;
  private sottoscrizione?: Subscription;
  private modale: HTMLIonModalElement | null = null;
  /** Incrementato a ogni cambio di contesto: scarta una presentazione rimasta a metà di un contesto superato. */
  private generazione = 0;
  /** Verifica (chiamata + attesa della pagina) in corso per il contesto corrente. */
  private inVerifica = false;
  private readonly _occupato$ = new BehaviorSubject<boolean>(false);
  /**
   * `true` mentre il gate sta verificando o ha la modale aperta: chi vuole presentare un proprio
   * overlay all'avvio (es. il promemoria debiti mensa) aspetta che torni `false`.
   */
  readonly occupato$: Observable<boolean> = this._occupato$.asObservable();

  constructor(
    private readonly api: DocumentiComuneApi,
    private readonly localStorageService: LocalStorageService,
    private readonly globalManagerComune: GlobalManagerComuneService,
    private readonly logoutManager: LogoutManagerService,
    private readonly modalController: ModalController,
    private readonly router: Router,
  ) {}

  /** Avvia l'ascolto. Idempotente: da chiamare una volta, da `AppComponent.ngOnInit`. */
  avvia(): void {
    if (this.avviato) {
      return;
    }
    this.avviato = true;
    this.sottoscrizione = combineLatest([
      this.localStorageService.checkLoggedUser$,
      this.globalManagerComune.comuneSelezionato$,
    ])
      .pipe(
        map(([loggato, comune]) => this.contesto(loggato, comune?.slug)),
        distinctUntilChanged((a, b) => a?.userId === b?.userId && a?.slug === b?.slug),
        // Il contesto è cambiato (logout, altro Comune, altro utente): una modale ancora aperta
        // parla di un contesto che non c'è più.
        tap((ctx) => {
          this.generazione++;
          void this.chiudiModale();
          this.inVerifica = ctx !== null;
          this.aggiornaOccupato();
        }),
        switchMap((ctx) => (ctx ? this.verifica$(ctx) : EMPTY)),
      )
      .subscribe({
        error: (err) => console.warn('[DocumentiComuneGate] errore inatteso, il gate si ferma', err),
      });
  }

  /** Ferma l'ascolto e chiude la modale (usato dai test). */
  ferma(): void {
    this.sottoscrizione?.unsubscribe();
    this.avviato = false;
    this.inVerifica = false;
    void this.chiudiModale();
    this.aggiornaOccupato();
  }

  private aggiornaOccupato(): void {
    const occupato = this.inVerifica || this.modale !== null;
    if (occupato !== this._occupato$.value) {
      this._occupato$.next(occupato);
    }
  }

  private contesto(loggato: boolean, slug: string | undefined): ContestoGate | null {
    if (!loggato || !slug) {
      return null;
    }
    const claims = this.localStorageService.decodeClaims();
    if (deveSaltareProfiloCittadino(claims?.roles)) {
      return null;
    }
    return {userId: claims?.id ?? claims?.email ?? '', slug};
  }

  private verifica$(ctx: ContestoGate): Observable<void> {
    const generazione = this.generazione;
    return this.api.daAccettare$(ctx.slug).pipe(
      // Fail-open: ogni errore equivale a «niente da accettare», senza modale né navigazione.
      catchError((err) => {
        console.warn('[DocumentiComuneGate] verifica documenti non riuscita, si prosegue', err?.status);
        return EMPTY;
      }),
      filter((stato: DocumentiDaAccettare) => stato?.da_accettare === true && stato.documenti?.length > 0),
      switchMap((stato) =>
        this.attendiPaginaAmmessa$().pipe(
          // Nessuna pagina d'arrivo ammessa entro il limite: meglio non mostrare nulla che una
          // modale sopra una navigazione (o una pagina di accesso) che la annullerebbe.
          catchError(() => EMPTY),
          switchMap(() => this.presenta(ctx, stato)),
        ),
      ),
      finalize(() => {
        if (generazione === this.generazione) {
          this.inVerifica = false;
          this.aggiornaOccupato();
        }
      }),
    );
  }

  /**
   * Emette (una volta) quando la prima navigazione è conclusa, nessun'altra è in corso, il router è su
   * una pagina d'arrivo ammessa e il profilo cittadino è caricato. Altrimenti aspetta la prossima
   * navigazione. Il `debounceTime(0)` lascia partire una navigazione concatenata (redirect) prima di
   * ricontrollare.
   */
  private attendiPaginaAmmessa$(): Observable<void> {
    return defer(() =>
      this.router.events.pipe(
        filter((e) => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError),
        startWith(null),
        debounceTime(0),
        filter(
          () =>
            this.router.navigated &&
            this.router.getCurrentNavigation() === null &&
            this.paginaAmmessa() &&
            !!this.localStorageService.getUserProfile(),
        ),
        take(1),
        timeout({first: ATTESA_ROUTER_MAX_MS}),
        map(() => undefined),
      ),
    );
  }

  private paginaAmmessa(): boolean {
    return ROTTA_AMMESSA.test((this.router.url ?? '').split(/[?#]/)[0]);
  }

  private presenta(ctx: ContestoGate, stato: DocumentiDaAccettare): Observable<void> {
    return defer(async () => {
      // Mai due modali del gate insieme.
      if (this.modale) {
        return;
      }
      const generazione = this.generazione;
      const trigger = document.activeElement as HTMLElement | null;
      const modal = await this.modalController.create({
        id: DOCUMENTI_COMUNE_GATE_MODAL_ID,
        component: DocumentiComuneModalComponent,
        componentProps: {
          modalita: 'gate',
          idModale: DOCUMENTI_COMUNE_GATE_MODAL_ID,
          slug: ctx.slug,
          documenti: stato.documenti,
          // Il tasto indietro e le navigazioni non devono chiuderla...
          isNotDismissableWithBack: true,
          // ...e le navigazioni automatiche (auto-skip) non devono nemmeno essere annullate da lei.
          isAutoSkipSafe: true,
        },
        backdropDismiss: false,
        // Gesture, backdrop ed Esc non chiudono. Resta ammessa la dismiss programmatica senza
        // ruolo: è quella di `NotificationService.closeAll()` nei teardown di sessione (logout,
        // 401), che altrimenti ritenterebbe all'infinito su una modale che non si lascia chiudere.
        canDismiss: async (_data?: unknown, role?: string) => role !== 'backdrop' && role !== 'gesture',
        htmlAttributes: {'aria-labelledby': 'modal-title'},
      });
      if (generazione !== this.generazione) {
        // Il contesto è cambiato durante create(): l'elemento è già nel DOM, lo si toglie.
        modal.remove();
        return;
      }
      this.modale = modal;
      this.aggiornaOccupato();
      await modal.present();
      // Primo focus sull'introduzione (non su «Esci», primo elemento focalizzabile): WCAG 2.4.3.
      document.getElementById('documenti-comune-intro')?.focus();
      void modal.onDidDismiss().then(({role}) => {
        if (this.modale === modal) {
          this.modale = null;
          this.aggiornaOccupato();
        }
        if (role === DOCUMENTI_COMUNE_ESITO_ESCI) {
          // Prima si chiude la modale, poi si esce: il logout naviga con `loggingOut`.
          this.logoutManager.logout();
          return;
        }
        if (role === DOCUMENTI_COMUNE_ESITO_ACCETTATO) {
          this.restituisciFocus(trigger);
        }
      });
    }).pipe(
      catchError((err) => {
        this.modale = null;
        this.aggiornaOccupato();
        console.warn('[DocumentiComuneGate] apertura modale non riuscita, si prosegue', err);
        return EMPTY;
      }),
    );
  }

  private async chiudiModale(): Promise<void> {
    const modal = this.modale;
    if (!modal) {
      return;
    }
    this.modale = null;
    this.aggiornaOccupato();
    try {
      await modal.dismiss(null, DOCUMENTI_COMUNE_ESITO_SISTEMA);
    } catch {
      // già chiusa
    }
  }

  /**
   * Alla chiusura per accettazione il focus torna dove era; la modale si apre da sola, quindi se
   * l'elemento non c'è più (o era il body) lo si porta al titolo della pagina (WCAG 2.4.3).
   */
  private restituisciFocus(trigger: HTMLElement | null): void {
    setTimeout(() => {
      const attivo = document.activeElement;
      if (attivo && attivo !== document.body) {
        return;
      }
      if (trigger && trigger !== document.body && document.contains(trigger)) {
        trigger.focus();
        return;
      }
      document.querySelector<HTMLElement>('#page-header-title, #main-content h1')?.focus();
    });
  }
}
